// The Suggested tray (spec §7): findings an agent suggested, newest first. A person accepts one (onto
// Research, into the Inbox column unless dragged onto a board) or dismisses it. It updates live: an
// agent's add_finding appears here while everyone watches.
//
// Collapse (Peter's request before the M2 deploy): the tray can fold to a slim tab on the right edge
// so the canvas gets the width back. The collapsed/expanded choice is per browser (localStorage key
// `plana.trayCollapsed`). With no stored choice, it defaults to collapsed when there's nothing
// suggested and expanded when there is — but once a person has explicitly toggled it, that choice
// sticks even as findings arrive or clear, so a live finding never yanks focus by auto-expanding.
import { useState } from 'react';
import { boardsMap, cardsMap, cleanTags, parseCard } from '../../shared/doc.js';
import { acceptFinding, dismissFinding, restoreFinding, trayFindings, updateCard } from '../../shared/ops.js';
import { CARD_DRAG_TYPE } from '../canvas/Canvas.js';
import { FindingBody, StatusPicker, type Finding } from '../cards/FindingCard.js';
import type { ProjectConn } from '../doc/useProject.js';
import { useYVersion } from '../doc/useY.js';

/**
 * The tag editor for one finding. Keyed (while not focused) on the card's own tags, so a remote
 * change — another member, or an agent's update_card — replaces the field's defaultValue instead of
 * being silently clobbered on the next blur; keyed on a stable token while focused, so typing isn't
 * interrupted by someone else's concurrent edit. Only writes on blur when the cleaned value actually
 * differs from the card's tags, so clicking in and back out never stamps an empty no-op edit.
 */
function TagsEditor({ conn, card }: { conn: ProjectConn; card: Finding }) {
  const [focused, setFocused] = useState(false);
  return (
    <label className="tags">
      Tags{' '}
      <input
        key={focused ? 'editing' : card.tags.join('|')}
        defaultValue={card.tags.join(', ')}
        placeholder="ferry, november"
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          const next = cleanTags(e.target.value.split(','));
          if (next.join('|') !== card.tags.join('|')) updateCard(conn.doc, conn.me, card.id, { tags: next });
        }}
      />
    </label>
  );
}

const COLLAPSE_KEY = 'plana.trayCollapsed';

/**
 * The explicit choice stored from a previous visit, or null if the person has never toggled it
 * (or storage is blocked — Safari private mode and some browser policies make `localStorage`
 * throw `SecurityError` just for touching it, and that must not take the whole page down with it).
 */
function storedCollapsed(): boolean | null {
  try {
    const v = localStorage.getItem(COLLAPSE_KEY);
    return v === 'true' ? true : v === 'false' ? false : null;
  } catch {
    return null;
  }
}

/** Best-effort: persisting the choice is nice to have, but a blocked store must not break the toggle. */
function storeCollapsed(next: boolean) {
  try {
    localStorage.setItem(COLLAPSE_KEY, String(next));
  } catch {
    // storage blocked (e.g. SecurityError) — the in-memory state below still toggles.
  }
}

export function Tray({ conn, onOpenCard }: { conn: ProjectConn; onOpenCard: (cardId: string) => void }) {
  useYVersion(cardsMap(conn.doc));
  useYVersion(boardsMap(conn.doc)); // a finding placed on a board leaves the tray
  const { suggested, dismissed } = trayFindings(conn.doc);
  const findings = (ids: string[]) => ids.map((id) => parseCard(cardsMap(conn.doc).get(id)?.toJSON())).filter((c): c is Finding => c?.kind === 'finding');
  // Counts (and the "Nothing suggested" check) match what's actually rendered below: a finding
  // whose data fails parseCard (a future version, a bug) is dropped from the list, so the badge
  // must drop it too rather than promising more items than the tray shows.
  const suggestedFindings = findings(suggested);

  // Lazy-initialised once per mount: a stored explicit choice wins; otherwise the default follows
  // whether there's anything suggested *at mount time*. Later finding counts don't move this state
  // (see the module comment) — only clicking the tab or the collapse button does, and that click
  // also persists the choice so it survives a reload.
  const [collapsed, setCollapsed] = useState(() => storedCollapsed() ?? suggestedFindings.length === 0);
  const toggle = (next: boolean) => {
    storeCollapsed(next);
    setCollapsed(next);
  };

  if (collapsed) {
    return (
      <aside className="tray collapsed" aria-label="Suggested">
        <button className="tray-tab" aria-expanded={false} aria-label={`Suggested (${suggestedFindings.length})`} onClick={() => toggle(false)}>
          <span className="tray-tab-label">Suggested</span>
          <span className="count">{suggestedFindings.length}</span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="tray" aria-label="Suggested">
      <h2>
        Suggested <span className="count">{suggestedFindings.length}</span>
        <span className="spacer" />
        <button className="link small" aria-label="Collapse Suggested" onClick={() => toggle(true)}>
          »
        </button>
      </h2>
      {suggestedFindings.length === 0 && <p className="muted">Nothing suggested. Findings from your agent land here.</p>}
      <ol className="suggested">
        {suggestedFindings.map((f) => (
          <li
            key={f.id}
            data-card-id={f.id}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(CARD_DRAG_TYPE, f.id);
              e.dataTransfer.effectAllowed = 'move';
            }}
          >
            <FindingBody card={f} />
            <p className="muted small">
              {f.createdBy.kind === 'agent' ? '🤖 ' : ''}
              {f.createdBy.name}
            </p>
            <StatusPicker conn={conn} card={f} />
            <TagsEditor conn={conn} card={f} />
            <div className="actions">
              <button onClick={() => acceptFinding(conn.doc, conn.me, f.id)}>Accept</button>
              <button className="link" onClick={() => dismissFinding(conn.doc, conn.me, f.id)}>
                Dismiss
              </button>
              <span className="spacer" />
              <button className="link small" aria-label="Open card" onClick={() => onOpenCard(f.id)}>
                ⤢
              </button>
            </div>
          </li>
        ))}
      </ol>
      {dismissed.length > 0 && (
        <details>
          <summary>Dismissed ({dismissed.length})</summary>
          <ol className="dismissed">
            {findings(dismissed).map((f) => (
              <li key={f.id} data-card-id={f.id}>
                <p>{f.claim}</p>
                <button className="link small" onClick={() => restoreFinding(conn.doc, conn.me, f.id)}>
                  Back to tray
                </button>
              </li>
            ))}
          </ol>
        </details>
      )}
    </aside>
  );
}
